import Context from '@/ViewModels/Context';
import ComponentManager from '@/Management/ComponentManager';
import GenericObject from '@/Model/GenericObject';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import {DisposeBehavior, IValidationMessage, ResultDTO} from '#/DefaultViewExtensionInterfaces';
import UIListener from '@/UIListener/UIListener';
import RADApplication from '@/Management/RADApplication';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import ControlFactory from '@/Factories/ControlFactory';
import ResourceManager from '@/Management/ResourceManager';
import Messenger from '@/Utility/Messenger';
import type DetailViewContext from '@/ViewModels/Contexts/DetailViewContext';
import UIListenerHelper from '@/UIListener/UIListenerHelper';

export default class EditContext extends Context {

    /**
    * Der übergeordnete DetailViewContext bei einem NestedContext / InlineContext
    */
    protected __parent: DetailViewContext;

    /**
     * The current generic object
     */
    public genericObject: GenericObject;

    /**
     * The id of the current lockgroup
     */
    protected lockGroupID: number;

    /**
     * Standard-Iteratoren zur initialen Verwendung in ControlFactories
     * Key: Name des Items, Value: IteratorDTO
     */
    public defaultIterators: Record<string, CoreFrameDTOs.IIteratorDTO>;

    /**
     * The DTO of the current generic object
     */
    protected subject: CoreFrameDTOs.IGenericDTO;

    /**
     * The current UIListener
     */
    public uiListener: UIListener;

    /**
     * Properties, which were updated by server
     */ 
    protected _updatedByServer: Array<string>;

    /**
     * Property values, which were updated by server
     */
    protected _updatedByServerValues: Array<string>;

    /**
     * Registered validation callbacks from control factories
     */
    public onValidationError: Array<(itemName: string, validationMessage: string, isValid: boolean) => void>;

    public isValid: KnockoutComputed<boolean>;

    public constructor(params: any) {
        super(params);

        this.__parent = params.parent;
        this._updatedByServer = [];
        this._updatedByServerValues = [];
        this.subject = this.paramOrDefault(params.subject);
        this.genericObject = null;
        this.onValidationError = [];
        this.lockGroupID = parseInt(this.paramOrDefault(params.lockGroupID));
        this.defaultIterators = this.paramOrDefault(params.defaultIterators);
        this.uiListener = Utilities.isNullOrUndefinedOrBlank(params.uiListener) ? null : UIListener.createInstance(params.uiListener);
        this.isValid = this.defineComputed(() => {
            let createdItems = this._createdItems();
            for (let i = 0; i < Object.keys(createdItems).length; i++) {
                let key = Object.keys(createdItems)[i];
                let createdItem = createdItems[key];
                if (!createdItem || createdItem.isDisposed === true)
                    return false;
                
                let factory = createdItem.factory;
                if (!factory || factory.isDisposed === true)
                    return false;

                if (typeof (factory.isValid) === "function" && factory.isValid() === false) {
                    return false;
                }
            }
            return true;
        });
        

        this.init();
    }

    /**
     * Sets generic object of parent context to isModified
     * Taking over the child context must not reset the state of the parent.
     * Therefore this has to be done explicitly and not derived.
     */
    private registerSubscriptionToNotifyParent(): void {
        this.defineSubscription(this.genericObject.isModified, (isModified) => {
            if (isModified) {
                this.getParentContext()?.getRootDetailViewContext()?.genericObject?.isModified(true);
            }
        });
    }

    /**
     * Informiert den UIListener darüber, dass eine Assoziation verändert wird
     * @param relation
     * @param actionType
     * @param additionalParameters
     */
    public notifyBeforeUpdatedAssociation(relation: string, actionType: string, additionalParameters: { toPdoIds: Array<string> }): JQueryDeferred<boolean> | JQueryPromise<boolean> {
        return this.uiListener?.onBeforeAssociationUpdated(this, relation, actionType, additionalParameters);
    }

    /**
     * Informiert den UIListener über eine aktualisierte Assoziation
     * @param relation
     */
    public notifyUpdatedAssociation(relation: string): void {
        this.genericObject.forceUpdateProperties();
        (this.uiListener as any)?.onAssociationUpdatedInternal(this, relation);
    }

    /**
     * Versendet eine FactoryRefreshedMessage mit Auskunft über das aktualisierte DetailViewItem
     * @param detailViewItem
     */
    public notifyFactoryRefreshed(detailViewItem: CoreFrameDTOs.IDVGItemDTO): void {
        Messenger.createMessageAndSend('FactoryRefreshedMessage', { item: detailViewItem });
    }

    /***
     * Ruft den aktuellen NestedContextPath ab,
     * @returns {string} - {a}.{b}.{c}
     */
    public getNestedContextPath(): string {
        return '';
    }

    /**
     * Gibt an, ob das GenericObject offene Änderungen hat
     */
    public hasPendingModifications(): boolean {
        return this.genericObject.hasPendingModifications();
    }

    /**
     * Sends uncommitted changes to the server
     */
    public updateProperties(): JQueryPromise<boolean> {
        const pendingChanges = this.genericObject.changes();

        return RADEnvironmentProxy.getInstance().updateProperties({
            pdoID: this.genericObject.pdoId,
            values: JSON.stringify(pendingChanges),
            scopedProperties: [],
            nestedContextName: this.getNestedContextPath()
        }).then((updateDto) => {
            const result = this.handleUpdateDTO(updateDto);
            if (result.isSuccessful || result.hasValidationMessages) {
                const toCommit = this.getPropertiesToCommitAfterUpdate(pendingChanges);
                this.genericObject.commit(toCommit);

                if (result.hasValidationMessages)
                    this.showValidationMessages(result.validationMessages, false);
            }

            if (result.isSuccessful
                && this.getParentContext().getRootDetailViewContext().useShortTransactions()) {
                this.resetGenericObjectsModifiedStatus();
            }

            return result.isSuccessful;
        }).promise();
    }

    protected getPropertiesToCommitAfterUpdate(sendChanges: Object): Array<string> {
        let ret = [];
        let keys = _.keys(sendChanges);
        keys.forEach((key) => {
            let befor = sendChanges[key];
            let know = this.genericObject.getValueReturnNullIfNotExists(key);

            if (befor === know) {
                ret.push(key);
            }
        });

        return ret;
    }

    /**
     * Resets all validation errors
     */
    protected resetValidationErrors(): void {
        for (let j = 0; j < this.onValidationError.length; j++) {
            this.onValidationError[j]('', '', true);
        }
    }

    /**
     * Sends uncommited changes to the server and triggers validation
     * @param propertyNames
     */
    public updateAndValidateProperties(propertyNames?: Array<string>): JQueryPromise<boolean> {
        const pendingChanges = this.genericObject.changes();

        this.resetValidationErrors();

        return RADEnvironmentProxy.getInstance().updateAndValidatePropertiesInGenericObject({
            pdoID: this.genericObject.pdoId,
            lockGroupID: this.lockGroupID.toString(),
            valuesToUpdate: JSON.stringify(pendingChanges),
            valuesToValidate: [],
            nestedContextName: this.getNestedContextPath()
        }).then((updateAndValidateDto) => {
            const result = this.handleUpdateAndValidateDTO(updateAndValidateDto);
            if (result.isSuccessful || result.hasValidationMessages) { 
                const toCommit = this.getPropertiesToCommitAfterUpdate(pendingChanges);
                this.genericObject.commit(toCommit);
            }

            if (result.hasValidationMessages)
                this.showValidationMessages(result.validationMessages, false);

            if (result.isSuccessful
                && this.getParentContext().getRootDetailViewContext().useShortTransactions()) {
                this.resetGenericObjectsModifiedStatus();
            }

            return result.isSuccessful;
        }).promise();
    }

    /**
     * Setzt das Flag "isModified" des GenericObjects auf false
     */
    protected resetGenericObjectsModifiedStatus(): void {
        this.resetGenericObjectsModifiedStatusInternal();
    }

    /**
     * Interne Methode für "resetGenericObjectsModifiedStatus"
     */
    protected resetGenericObjectsModifiedStatusInternal(): void {
        this.genericObject.isModified(false);
    }

    public init(): void {
        if (this.subject) {
            // Das delay darf bei short transaction nicht zu kurz sein, sonst wird jeder Tastenanschlag sofort in einen commit und einem Journaleintrag münden.
            // Bei einem großen Delay haben wir jedoch das Problem, das das Grid offene Änderungen nicht an den Server sendet bevor eine Aktion wie Refresh oder Page-Wechsel durchgeführt wird.
            // Das kleine Delay kaschiert diesen Fehler. // CF-92829
            const delay = this.getParentContext().getRootDetailViewContext().useShortTransactions() ? 1000 : 100;
            this.genericObject = new GenericObject(this.subject, delay);

            this.genericObject.onDelayedChange(() => {
                this.updateProperties();
            }, this);

            this.registerSubscriptionToNotifyParent()
        }
    }

    /**
     * Returns the id of lockgroup
     */
    public getLockGroupId(): number {
        return this.lockGroupID;
    }

    /**
     * Handles an UpdateAndValidateDTO
     * @param updateAndValidateDTO
     */
    protected handleUpdateAndValidateDTO(updateAndValidateDTO: CoreFrameDTOs.IUpdateAndValidateDTO): ResultDTO {
        let updateResult: ResultDTO;

        if (updateAndValidateDTO.updateDTO) {
            updateResult = this.handleUpdateDTO(updateAndValidateDTO.updateDTO);
            if (!updateResult.isSuccessful && !updateResult.hasValidationMessages)
                return updateResult;
        }

        if (updateAndValidateDTO.saveResultDTO) {
            const saveResult = this.createResultDTO(updateAndValidateDTO.saveResultDTO);

            const messages = updateResult
                ? this.mergeMessages(updateResult.validationMessages, saveResult.validationMessages)
                : saveResult.validationMessages;

            const isSuccessful = updateResult
                ? updateResult.isSuccessful && saveResult.isSuccessful
                : saveResult.isSuccessful;

            return {
                isSuccessful: isSuccessful,
                validationMessages: messages,
                hasValidationMessages: messages.length > 0
            }
        }
       
        return {
            isSuccessful: this.validateStatus(updateAndValidateDTO),
            hasValidationMessages: false,
            validationMessages: []
        };
    }

    private mergeMessages(updateMessages: IValidationMessage[], saveMessages: IValidationMessage[]): IValidationMessage[] {
        let result: IValidationMessage[] = [];

        if (updateMessages) {
            for (let message of updateMessages) {
                result.push(message);
            }
        }

        if (!saveMessages)
            return result;

        let toAdd: IValidationMessage[] = [];
        for (let message of saveMessages) {
            const propertyExist = result.filter(x => x.property == message.property).length > 0;
            if (propertyExist)
                continue;

            toAdd.push(message);
        }

        for (let message of toAdd) {
            result.push(message);
        }

        return result;
    }

    /**
     * Handles an UpdateDTO
     * @param updateDTO
     */
    protected handleUpdateDTO(updateDTO: CoreFrameDTOs.IUpdateDTO): ResultDTO {
        const result = this.createResultDTO(updateDTO);
        if (result.isSuccessful || result.hasValidationMessages) {
            this.updateGenericObjectViaUpdateDTO(updateDTO);
        }

        return result;
    }

    private updateGenericObjectViaUpdateDTO(updateDTO: CoreFrameDTOs.IUpdateDTO): void {
        let hasRefreshedProperties = updateDTO && _.keys(updateDTO.refreshProperties).length > 0;
        if (!hasRefreshedProperties)
            return;

        for (let property in updateDTO.refreshProperties) {
            let value = updateDTO.refreshProperties[property];
            let observable = this.genericObject.getObservable(property);

            observable(value);
            observable.commit();
            this.addPropertyUpdatedByServer(property, value);
        }

        this.uiListener?.onGenericObjectUpdated(this, updateDTO.refreshProperties);
    }

    /**
     * Handles a SaveResultDTO or UpdateDTO
     * @param resultDto
     */
    private createResultDTO(resultDto: CoreFrameDTOs.ISaveResultDTO | CoreFrameDTOs.IUpdateDTO): ResultDTO {
        if (resultDto.status === 'Warning' && !resultDto.isValid && _.keys(resultDto.validationMessages).length > 0) {
            const messages = this.convertValidationObject(resultDto.validationMessages);
            const hasMessages = messages.length > 0;
            return {
                isSuccessful: !hasMessages,
                hasValidationMessages: hasMessages,
                validationMessages: messages
            };
        }

        return {
            isSuccessful: this.validateStatus(resultDto),
            hasValidationMessages: false,
            validationMessages: []
        };
    }

    /**
     * Converts a validation message object to an array of structured validation messages
     * @param validationMessages
     */
    protected convertValidationObject(validationMessages: Object): IValidationMessage[] {
        let resultArray = [];
        _.keys(validationMessages).forEach(key => {
            resultArray.push({ context: this, property: key, message: validationMessages[key] });
        });

        return resultArray;
    }

    public setValidationErrors(validationMessages: IValidationMessage[]): void {
        this.resetValidationErrors();
        this.showValidationMessages(validationMessages, false);
    }

    /**
    * Show the validation messages and shows messagebox (per parameter)
    * @param validationMessages Array of validation messages
    */
    protected showValidationMessages(validationMessages: IValidationMessage[], showMessageBox: boolean = true): boolean {
        const hasValidationMessages = validationMessages.length > 0;

        if (!hasValidationMessages)
            return false;
        
        for (let i = 0; i < _.keys(validationMessages).length; i++) {
            const currentMessage = validationMessages[i];

            for (let j = 0; j < this.onValidationError.length; j++) {
                this.onValidationError[j](currentMessage.property, currentMessage.message, false);
            }
        }

        if (showMessageBox) {
            const itemNames = this.extractItemNames(validationMessages);
            RADApplication.getInstance().showMessageBox(MessageBoxViewModel.createValidationMessageBox(itemNames));
        }

        return true;
    }

    private addPropertyUpdatedByServer(name: string, value: any): void {
        if (this._updatedByServer.indexOf(name) == -1) {
            this._updatedByServer.push(name);
        }
        this._updatedByServerValues[name] = value;
    }

    /**
     * Extracts property names from validation messages
     * @param validationMessages An array of validation messages
     */
    protected extractItemNames(validationMessages: IValidationMessage[]): Array<string> {
        // one message per property per context
        validationMessages = _.uniq(validationMessages, false, (m) => { return `${m.context.getNestedContextPath()}_${m.property}`; });
        const itemNames = validationMessages.map(vm => {
            const factory = vm.context.getUIFactory(vm.property) as ControlFactory;
            // factory is not instantiated, e.g. in other wizard group pages
            if (Utilities.isNullOrUndefined(factory)) {
                const dvgItem = UIListenerHelper.getDetailViewItemDto(vm.context, vm.property);
                // there is no item with this name
                if (Utilities.isNullOrUndefined(dvgItem))
                    return null;

                return ResourceManager.getInstance().get(dvgItem.ergName);
            }
                
            return ResourceManager.getInstance().get(factory.getErgNameForValidationMessage());
        })
            .filter(f => f != null);

        return itemNames;
    }

    /**
     * Returns the current ui listener
     */
    public getUIListener(): UIListener {
        return this.uiListener;
    }

    /**
     * Gibt den übergeordneten Kontext zurück
     */
    public getParentContext(): DetailViewContext {
        return this.__parent;
    }

    public isSummaryViewMode(): boolean {
        return this.getParentContext()?.isSummaryViewMode() || false;
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setViewModel('EditContext', EditContext, 'RADPresentation.HTMLClient.DefaultViewExtensions');